<template>
  <div class="chart-bar" ref="chartdiv" :style="{ height: chartSettings.chartHeight ? chartSettings.chartHeight + 'px' : '400px' }"></div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import * as _ from 'underscore'
import { mapGetters } from 'vuex'

export default {
  name: 'chartBarRender',
  props: {
    chartKey: { type: String, required: true },
    fromDialog: { type: Boolean, required: false }
  },
  data () {
    return {
      chartSettings: {},
      chartData: []
    }
  },
  computed: {
    ...mapGetters(['translation']),
    chartObj: {
      get () { return this.$store.getters.getChartByKey(this.chartKey) }
    }
  },
  mounted () {
    let self = this
    this.chartSettings = this.chartObj.chartSettings
    this.chartData = this.chartObj.data.result

    // check and reduce data if not in dialog
    if (!this.fromDialog && this.chartData.length > (this.chartObj.chartSettings.displayLength || 10)) {
      const reducedData = this.chartData.filter((data, index) => index < (this.chartObj.chartSettings.displayLength || 10))
      // this.$log.info('reducedData', reducedData)
      this.chartData = reducedData
    }

    let chart = am4core.create(this.$refs.chartdiv, am4charts.XYChart)
    chart.paddingRight = 20
    chart.maskBullets = false // if true, LabelBullets will be outside graph
    // chart.responsive.enabled = true

    // Add data
    chart.data = this.chartData

    // Enable export if 'true' in chart setting
    /* if (this.chartObj.export) {
      chart.events.on('ready', () => {
        chart.exporting.menu = new am4core.ExportMenu()
        chart.exporting.menu.align = 'right'
        chart.exporting.menu.verticalAlign = 'bottom'
        chart.exporting.menu.items = [{
          'label': '...',
          'menu': [
            {
              'label': 'Image',
              'menu': [
                { 'type': 'png', 'label': 'PNG' },
                { 'type': 'jpg', 'label': 'JPG' },
                { 'type': 'gif', 'label': 'GIF' },
                { 'type': 'svg', 'label': 'SVG' },
                { 'type': 'pdf', 'label': 'PDF' }
              ]
            }, {
              'label': 'Data',
              'menu': [
                // { 'type': 'json', 'label': 'JSON' },
                { 'type': 'csv', 'label': 'CSV' }
                // { 'type': 'xlsx', 'label': 'XLSX' }
              ]
            }, {
              'label': 'Print', 'type': 'print'
            }
          ]
        }]
      })
    } */

    let categoryAxis
    let valueAxis
    // Create axes
    // Check for chart rotation
    if (self.chartSettings.rotate) {
      categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis())
      categoryAxis.renderer.inversed = true
      valueAxis = chart.xAxes.push(new am4charts.ValueAxis())
      valueAxis.renderer.opposite = true
    } else {
      categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis())
      valueAxis = chart.yAxes.push(new am4charts.ValueAxis())
    }

    if (self.chartSettings.negativeValues) {
      valueAxis.max = 0
    } else {
      valueAxis.min = 0
    }

    categoryAxis.dataFields.category = 'name'
    categoryAxis.renderer.grid.template.location = 0
    categoryAxis.renderer.minGridDistance = 30

    function createSeries (field, name) {
      let series
      // Create series
      series = chart.series.push(new am4charts.ColumnSeries())
      self.chartSettings.rotate ? series.dataFields.valueX = field : series.dataFields.valueY = field
      self.chartSettings.rotate ? series.dataFields.categoryY = 'name' : series.dataFields.categoryX = 'name'
      series.name = name
      series.calculatePercent = true
      if (self.chartSettings.tooltipWithoutPercentage) {
        self.chartSettings.rotate
          ? series.columns.template.tooltipText = `{sub_name} {categoryY}: [bold]{valueX}[/]`
          : series.columns.template.tooltipText = `{sub_name} {categoryX}: [bold]{valueY}[/]`
      } else {
        self.chartSettings.rotate
          ? series.columns.template.tooltipText = `{sub_name} {categoryY}: [bold]{valueX}[/] ({valueX.percent.formatNumber("#.#")}%)`
          : series.columns.template.tooltipText = `{sub_name} {categoryX}: [bold]{valueY}[/] ({valueY.percent.formatNumber("#.#")}%)`
      }
      series.columns.template.fillOpacity = 0.25
      series.columns.template.maxHeight = 50
      series.columns.template.width = am4core.percent(self.chartSettings.barsWidth || 50)

      categoryAxis.renderer.grid.template.location = 0
      categoryAxis.renderer.minGridDistance = self.chartSettings.gridDistance || 25

      let categoryAxisLabel = categoryAxis.renderer.labels.template
      // if no label settings from dashboards use default
      categoryAxisLabel.rotation = self.chartSettings.labelRotation || 0
      categoryAxisLabel.horizontalCenter = self.chartSettings.labelHorizontalCenter || 'middle'
      categoryAxisLabel.verticalCenter = self.chartSettings.labelVerticalCenter || 'middle'
      // categoryAxisLabel.textAlign = self.chartSettings.textAlign || null
      self.chartSettings.truncate !== undefined
        ? categoryAxisLabel.truncate = self.chartSettings.truncate
        : categoryAxisLabel.truncate = true
      categoryAxisLabel.tooltipText = '{category}'
      // categoryAxisLabel.maxWidth = 120 // if no auto max-width below
      // alternative to maxWidth
      categoryAxis.events.on('sizechanged', ev => {
        let axis = ev.target
        axis.renderer.labels.template.maxWidth = self.chartSettings.maxLabelWidth || (axis.pixelWidth / (axis.endIndex - axis.startIndex))
      })

      let valueLabel = series.bullets.push(new am4charts.LabelBullet())
      valueLabel.label.text = '{value}'
      self.chartSettings.rotate ? valueLabel.label.dy = 0 : valueLabel.label.dy = -10
      self.chartSettings.rotate ? valueLabel.label.dx = 10 : valueLabel.label.dx = 0
      self.chartSettings.rotate ? valueLabel.label.horizontalCenter = 'left' : valueLabel.label.horizontalCenter = 'middle'
      valueLabel.label.truncate = self.chartSettings.valueLabelTruncate || false
      self.chartSettings.valueLabelTruncate !== undefined
        ? valueLabel.label.truncate = self.chartSettings.valueLabelTruncate
        : valueLabel.label.truncate = false
    }

    _.each(_.keys(this.chartData[0]), function (key) {
      if (key === 'name' || key === 'sub_name' || key === 'sort_column') {
        return
      }
      if (self.chartSettings.translation) {
        createSeries(key, self.translation(`chart.data.value.${key}`))
      } else {
        createSeries(key, key)
      }
    })

    // pre-zoom data for bigger length and enable zoom
    if (self.chartObj.chartSettings.chartZoom) {
      chart.scrollbarX = new am4core.Scrollbar()
    } else {
      if (chart.data.length > (self.chartObj.chartSettings.displayLength || 10)) {
        if (self.chartSettings.rotate) {
          chart.scrollbarY = new am4core.Scrollbar()
        } else {
          chart.scrollbarX = new am4core.Scrollbar()
          chart.events.on('ready', () => {
            let min = chart.xAxes.values[0].dataItems.values[0].category
            let max = chart.xAxes.values[0].dataItems.values[9].category
            categoryAxis.zoomToCategories(min, max)
          })
          // update categoryAxis labels size on scroll if horizontal
          if (categoryAxis.renderer.labels.template.rotation === 0) {
            categoryAxis.events.on('startchanged', categoryAxisZoomed)
            categoryAxis.events.on('endchanged', categoryAxisZoomed)
          }
        }
      }
    }

    function categoryAxisZoomed (ev) {
      let axis = ev.target
      let axisViewWidth = axis.axisLength
      let columnNr = axis.endIndex
      let startColumnInView = Math.round(axis.start * 100)
      let endColumnInView = Math.round(axis.end * 100)
      let columnsInView = Math.round(((endColumnInView - startColumnInView) * columnNr) / 100)
      axis.renderer.labels.template.maxWidth = axisViewWidth / columnsInView
    }

    if (self.chartObj.chartSettings.legend) {
      chart.legend = new am4charts.Legend()
    }
    /* // axis break
    let axisBreak = valueAxis.axisBreaks.create()
    axisBreak.startValue = 100
    axisBreak.endValue = 3100
    axisBreak.breakSize = 0.005

    // make break expand on hover
    let hoverState = axisBreak.states.create('hover')
    hoverState.properties.breakSize = 1
    hoverState.properties.opacity = 0.1
    hoverState.transitionDuration = 1500

    axisBreak.defaultState.transitionDuration = 1000 */

    this.chart = chart
  },
  methods: {},
  beforeDestroy () {
    if (this.chart) {
      this.chart.dispose()
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .chart-bar {
    width: 100%;
    height: 400px;
  }
</style>
